import axios from 'axios';
import { PASSENGERINFO } from '../components/Checkout';
import dayjs from 'dayjs';
import client from '../apollo-client';
import { gql } from '@apollo/client';
import { GET_PRODUCT_PRICES_BY_PRODUCT_ID } from '../api/privateTourPricesFromHasura';
import INSERT_AVAILABILITY_NOTIFICATION, {
  AVAILABILITY_NOTIFICATION_STATUS,
  BOATNEW_PRODUCTS_WITH_CITIES
} from '../api/availabilityShortage';
type axiosReturn = {
  data: PRODUCTAVAILABILITY[];
};
interface PRODUCTAVAILABILITY {
  id: number;
  startTime: string;
  endTime: string;
  allDay: boolean;
  seats: number;
  seatsAvailable: number;
  prices?: PASSENGERINFO[];
}
interface PRODUCTAVAILABILITY_WITH_HASURA_PRICES extends PRODUCTAVAILABILITY {
  boatnew_products_prices?: {
    product_id: string;
    prices: Record<string, string>;
    currency: string;
    __typename?: string;
  }[];
}
type FetchProductInterface = {
  rezdyId: string;
  startDate: any;
  privateTour?: boolean;
  tourName: string;
  cityName: string;
  slug?: string;
  productId?: string;
};
const mergePricesFromHasura = async (
  productAvailabilities: PRODUCTAVAILABILITY[],
  productId?: string
): Promise<PRODUCTAVAILABILITY_WITH_HASURA_PRICES[]> => {
  if (!productAvailabilities?.length) return productAvailabilities;

  const { data } = await client.query({
    query: GET_PRODUCT_PRICES_BY_PRODUCT_ID,
    variables: { productId },
    fetchPolicy: 'no-cache'
  });

  const hasuraPricesArray = data?.boatnew_products_prices;

  return productAvailabilities?.map(availability => ({
    ...availability,
    boatnew_products_prices: hasuraPricesArray || []
  }));
};

export const fetchProductAvailabilities = async ({
  rezdyId,
  startDate,
  privateTour,
  tourName,
  cityName,
  slug,
  productId
}: FetchProductInterface) => {
  const {
    data: { data: productAvailabilities }
  } = await axios.post<axiosReturn>(
    `${process.env.NEXT_PUBLIC_AVAILABILITY_URI}/availability`,
    {
      code: rezdyId,
      startDate: dayjs(startDate).startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs(startDate)
        .startOf('month')
        .add(2, 'month')
        .format('YYYY-MM-DD')
    },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!productAvailabilities) {
    const {
      data: { data: productAvailabilities }
    } = await axios.get<axiosReturn>(
      `${process.env.NEXT_PUBLIC_AVAILABILITY_URI}/firstAvailability/${rezdyId}`
    );
    return productAvailabilities;
  }

  const availabilitiesWithPrices = await mergePricesFromHasura(
    productAvailabilities,
    productId
  );
  if (privateTour) {
    return cleanPrivateAvailability(availabilitiesWithPrices, slug);
  }

  if (tourName !== undefined) {
    notifyShortage(availabilitiesWithPrices, tourName, cityName);
  }

  return availabilitiesWithPrices;
};

export const updateProductAvailabitoes = async ({
  rezdyId,
  startDate,
  privateTour,
  tourName,
  cityName,
  slug,
  productId
}: FetchProductInterface) => {
  const {
    data: { data: productAvailabilities }
  } = await axios.post<axiosReturn>(
    `${process.env.NEXT_PUBLIC_AVAILABILITY_URI}/availability`,
    {
      code: rezdyId,
      startDate: dayjs(startDate).startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs(startDate)
        .startOf('month')
        .add(1, 'month')
        .format('YYYY-MM-DD')
    },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  const availabilitiesWithPrices = await mergePricesFromHasura(
    productAvailabilities,
    productId
  );

  if (privateTour) {
    return cleanPrivateAvailability(availabilitiesWithPrices, slug);
  }

  notifyShortage(availabilitiesWithPrices, tourName, cityName);

  return availabilitiesWithPrices;
};

interface ProductAvailabilities {
  seatsAvailable: number;
  prices: [
    {
      label: string;
      price: number;
    }
  ];
}

const cleanPrivateAvailability = (
  productAvailabilities: PRODUCTAVAILABILITY[],
  slug?: string
) => {
  const cleanLessThan7 = productAvailabilities?.filter(
    availability =>
      availability.seatsAvailable >= 7 || slug === 'private-tour-of-chicago'
  );
  const adjustedPrices = cleanLessThan7?.map(availability => {
    const priceOptionsLimit = availability?.seatsAvailable;
    return {
      ...availability,
      prices: availability?.prices?.slice(0, priceOptionsLimit) ?? []
    };
  });

  return adjustedPrices;
};

const notifyShortage = async (
  productAvailabilities: PRODUCTAVAILABILITY[],
  tourName: string,
  cityName: string
) => {
  const shortage = productAvailabilities.filter(
    availability => availability.seatsAvailable <= 1
  );
  if (shortage.length > 0) {
    const { data } = await client.query({
      query: BOATNEW_PRODUCTS_WITH_CITIES
    });
    const managersEmails = Array.from(
      new Set(
        data.boatnew_products
          .flatMap((product: any) => product?.cities_products)
          .filter(
            (cityProduct: any) =>
              cityProduct?.city?.name?.trim().toLowerCase() ===
              cityName?.trim().toLowerCase()
          )
          .map((cityProduct: any) => cityProduct.city.ops_manager?.email)
          .filter((email: any) => email)
      )
    );
    for (const shortageItem of shortage) {
      const { id, seatsAvailable, seats } = shortageItem;

      try {
        const alreadyNotified = await client.query({
          query: AVAILABILITY_NOTIFICATION_STATUS,
          variables: { id }
        });
        if (alreadyNotified.data.boatnew_availability_shortage.length === 0) {
          await client.mutate({
            mutation: INSERT_AVAILABILITY_NOTIFICATION,
            variables: {
              id,
              seats_remaining: seatsAvailable,
              total_seats: seats,
              slot: `${dayjs(shortageItem.startTime).format('hh:mm A')}-${dayjs(
                shortageItem.endTime
              ).format('hh:mm A')} `,
              date: dayjs(shortageItem.startTime).format('YYYY-MM-DD'),
              tour: tourName,
              managerEmail: managersEmails[0],
              city: cityName
            }
          });
        }
      } catch (error) {
        console.error('Error inserting notification:', error);
      }
    }
  }
};
